import React, { useId } from 'react';
import { cn } from '../../lib/utils';
import { icons } from './Icons';

interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'info' | 'success' | 'warning' | 'error';
  title?: string;
  children: React.ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  dismissLabel?: string;
}

const variantClasses: Record<NonNullable<AlertProps['variant']>, string> = {
  info: 'border-primary-200 bg-primary-50 dark:border-primary-800 dark:bg-primary-900/20',
  success: 'border-success-200 bg-success-50 dark:border-success-800 dark:bg-success-900/20',
  warning: 'border-warning-200 bg-warning-50 dark:border-warning-800 dark:bg-warning-900/20',
  error: 'border-emergency-200 bg-emergency-50 dark:border-emergency-800 dark:bg-emergency-900/20',
};

const iconClasses: Record<NonNullable<AlertProps['variant']>, string> = {
  info: 'text-primary-600 dark:text-primary-400',
  success: 'text-success-700 dark:text-success-400',
  warning: 'text-warning-700 dark:text-warning-400',
  error: 'text-emergency-700 dark:text-emergency-400',
};

const iconMap: Record<NonNullable<AlertProps['variant']>, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  info: icons.Info,
  success: icons.CheckCircle,
  warning: icons.AlertTriangle,
  error: icons.AlertCircle,
};

export function Alert({
  variant = 'info',
  title,
  children,
  dismissible = false,
  onDismiss,
  dismissLabel = 'Hinweis schließen',
  className,
  id,
  ...props
}: AlertProps) {
  const generatedId = useId();
  const alertId = id ?? generatedId;
  const titleId = `${alertId}-title`;
  const contentId = `${alertId}-content`;
  const Icon = iconMap[variant];

  return (
    <div
      id={alertId}
      className={cn('flex gap-3 rounded-md border p-3.5', variantClasses[variant], className)}
      {...props}
      role="alert"
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={contentId}
    >
      <Icon className={cn('mt-0.5 h-5 w-5 flex-shrink-0', iconClasses[variant])} aria-hidden="true" />
      <div id={contentId} className="min-w-0 flex-1">
        {title && <h4 id={titleId} className="text-sm font-semibold leading-5 text-neutral-900 dark:text-neutral-100">{title}</h4>}
        <div className={cn('text-sm leading-5 text-neutral-700 dark:text-neutral-300', title && 'mt-1')}>
          {children}
        </div>
      </div>
      {dismissible && onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="ml-auto flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-neutral-500 transition-colors hover:bg-white/60 hover:text-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 focus-visible:ring-offset-2 dark:hover:bg-neutral-800 dark:hover:text-neutral-100 dark:focus-visible:ring-offset-neutral-900"
          aria-label={dismissLabel}
        >
          <icons.X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
